import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { Reveal } from "@/components/Motion";
import { Stars } from "@/components/Stars";
import { Quote, ArrowRight, PartyPopper, Star } from "lucide-react";
import { toast } from "sonner";

export default function Reviews() {
  const [data, setData] = useState({ reviews: [], count: 0, average: 0 });
  const [form, setForm] = useState({ name: "", location: "", rating: 5, text: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const load = () => api.get("/reviews").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.text.trim()) return toast.error("Please add your name and review");
    setBusy(true);
    try {
      await api.post("/reviews", form);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit review");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-[1100px] mx-auto px-5 md:px-10 pt-36 pb-28 min-h-screen">
      <Reveal>
        <span className="mono-label text-cyan">Customer reviews</span>
        <h1 className="font-display text-5xl md:text-7xl tracking-tight mt-4 mb-5">What reefers say</h1>
        <div className="flex items-center gap-4 flex-wrap">
          <Stars value={data.average} size={24} />
          <span className="font-mono text-2xl text-toxic">{data.average || "—"}</span>
          <span className="text-white/50">from {data.count} review{data.count === 1 ? "" : "s"}</span>
        </div>
        <a href={BRAND.googleReviewUrl} target="_blank" rel="noreferrer" data-testid="google-review-btn"
          className="btn-pill btn-solid inline-flex items-center gap-2 mt-6">
          <Star size={15} fill="#001014" /> Leave us a Google review
        </a>
      </Reveal>

      <div className="grid md:grid-cols-2 gap-6 mt-14">
        {data.reviews.map((r, i) => (
          <motion.div key={r.id} data-testid={`review-${r.id}`}
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6, delay: (i % 2) * 0.07 }}
            className="glass rounded-2xl p-7 border border-white/10 card-glow">
            <Quote className="text-cyan/40 mb-3" size={26} />
            <Stars value={r.rating} size={15} />
            <p className="text-white/75 mt-4 leading-relaxed">"{r.text}"</p>
            <div className="mt-5 pt-4 border-t border-white/10">
              <p className="font-display text-lg">{r.name}</p>
              {r.location && <p className="mono-label text-white/40">{r.location}</p>}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Leave a review */}
      <Reveal>
        <div className="mt-16 glass rounded-3xl p-8 md:p-12 border border-cyan/20" style={{ boxShadow: "0 30px 90px -40px rgba(0,240,255,0.3)" }} data-testid="review-form-card">
          {done ? (
            <div className="text-center py-8" data-testid="review-success">
              <PartyPopper className="mx-auto text-cyan mb-4" size={44} />
              <h2 className="font-display text-3xl mb-2">Thank you!</h2>
              <p className="text-white/60">Your review was submitted and will appear once approved. We appreciate you! 🐠</p>
            </div>
          ) : (
            <form onSubmit={submit}>
              <h2 className="font-display text-3xl mb-6">Leave a review</h2>
              <div className="mb-5">
                <label className="mono-label text-white/40 block mb-2">Your rating</label>
                <Stars value={form.rating} size={30} interactive onChange={(n) => setForm({ ...form, rating: n })} />
              </div>
              <div className="grid sm:grid-cols-2 gap-5 mb-5">
                <input className="er-input" placeholder="Your name" data-testid="review-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <input className="er-input" placeholder="City, State (optional)" data-testid="review-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
              </div>
              <textarea rows={4} className="er-input mb-6" placeholder="Tell others about your experience…" data-testid="review-text" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} />
              <button disabled={busy} data-testid="review-submit" className="btn-pill btn-solid flex items-center gap-2 disabled:opacity-50">
                {busy ? "Submitting…" : <>Submit review <ArrowRight size={15} /></>}
              </button>
            </form>
          )}
        </div>
      </Reveal>
    </div>
  );
}
